import { Type } from "@shared/ui/Type";
import { TextItem } from "@shared/ui/TextItem";
import { CallToAction } from "@shared/ui/CallToAction";
import halloranLogo from "@shared/assets/logos/logo_halloranPhilanthropies.png";
import jeromeLogo from "@shared/assets/logos/logo_jeromeFoundation.png";
import swissPhilanthropiesLogo from "@shared/assets/logos/logo_swissPhilanthropiesFoundation.png";
import { MarketingSection } from "../MarketingSection/MarketingSection";
import { MarketingAbout } from "../MarketingAbout/MarketingAbout";

type SectionItem = {
  title: string;
  description: string;
  subheader?: string;
};

type SectionsContent = {
  howItWorks: SectionItem[];
  fit: SectionItem[];
  praise: SectionItem[];
  team: SectionItem[];
  advisors: SectionItem[];
};

type MarketingContentProps = {
  onAddYourInstitutions: () => void;
  onDonateClick: () => void;
  onHireUsClick: () => void;
  onNewsletterClick: () => void;
  onNavigationAnchorClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string
  ) => void;
  sectionsContent: SectionsContent;
  sendingInstitutionItems: {
    name: string;
    location: string;
    isActive?: boolean;
  }[];
};

export function MarketingContent({
  onAddYourInstitutions,
  onDonateClick,
  onHireUsClick,
  onNewsletterClick,
  onNavigationAnchorClick,
  sectionsContent,
  sendingInstitutionItems,
}: MarketingContentProps) {
  return (
    <>
      <div className="flex flex-col gap-8">
        {/* VISION */}
        <MarketingSection
          title="We are building artist-led technology to weave relationships of mutual support."
          id="vision"
        >
          <Type variant="h4" className="max-w-xl">
            Why this? Why now?
          </Type>
          <Type variant="body1" className="max-w-xl">
            We believe that interdisciplinary practitioners across art, design,
            technology, and social impact have the desire and capacity to create
            opportunities for one another. Pollinator exists to build
            communities of solidarity across sectors, to make us all more
            resilient amidst crisis.
          </Type>
          <ul className="list-disc list-inside flex flex-col gap-6">
            <Type variant="body2" as="li" className="max-w-xl">
              Relationships drive access to opportunity. While cohort programs
              like residencies and art schools elevate careers temporarily,
              aligned peer groups sustain us. Great people want to meet and
              support other great people, and by joining forces, we can go
              farther together.
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              It <span className="italic">is</span> possible to resist the
              endless scroll, and to instead create digital spaces of generative
              conflict and belonging. While the nature of content engagement has
              changed, our desire to form bonds in real time has not. The power
              of human-to-human intelligence is great, and our minds come alive
              in group settings.
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              Lastly, we believe that you, our participants, will guide and
              support us as we continue to build Pollinator. We are here to
              create infrastructure that is made by, for, and with artists, in
              service of mutual support, and we value your contributions to this
              project.
            </Type>
          </ul>
          <Type variant="h4" className="max-w-xl">
            Your presence—being caring, curious, and open—is more transformative
            than your expertise.
          </Type>
          <Type variant="body1" className="max-w-xl">
            If Pollinator expanded its offerings to include additional features
            and platforms in 2027, what would you want?
          </Type>
          <ul className="list-disc list-inside flex flex-col gap-2">
            <Type variant="body2" as="li" className="max-w-xl">
              a program where we are matched and meet one time only,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              an in-person option for sessions,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a certificate in peer-coaching and jobs,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              access to expert coaches for 1:1 work,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a mentorship program and economy,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a LinkedIn-type platform,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a chat/Discord/Listserv-type community,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a resources-roundup newsletter,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a grant/funding/mutual aid program,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a directory of artist-run spaces, collectives, and more,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a place for grantmakers to find and fund participants,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              a social media platform by and for us,
            </Type>
            <Type variant="body2" as="li" className="max-w-xl">
              something else.
            </Type>
          </ul>
          <Type variant="body1" className="max-w-xl">
            Submit your ideas and requests to{" "}
            <a
              href="mailto:caroline@pollinator.coop"
              className="underline hover:text-brand-700"
            >
              caroline@pollinator.coop
            </a>
          </Type>
          <Type variant="body1" className="max-w-xl italic">
            If you love the container we are creating, please consider making a{" "}
            <a
              href="/#donate"
              className="underline hover:text-brand-700"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
                onNavigationAnchorClick(e, "donate")
              }
            >
              tax-deductible donation to support our work.
            </a>
          </Type>
        </MarketingSection>

        {/* PROGRAM */}
        <MarketingSection title="Program" id="program">
          <Type variant="body1" className="max-w-xl">
            Pollinator’s program is like a virtual residency of mutual support.
            Once enrolled, participants get matched with a curated circle of
            compatible peers. We handle all the logistics, and our
            artist-designed platform and conversation prompts help you build
            real relationships and creative momentum.
          </Type>
          <Type variant="body1" className="max-w-xl">
            Our 2025 pilot program was a success —{" "}
            <a
              href="https://pollinator-production.s3.us-east-1.amazonaws.com/production/assets/materials/PollinatorPilotClosingSession.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-brand-700"
            >
              read the report here
            </a>
            .
          </Type>
        </MarketingSection>
        {/* <MarketingSection title="How it Works" id="how-it-works">
          <div className="flex flex-col gap-4 md:gap-8">
            {sectionsContent.howItWorks.map(({ title, description }) => (
              <TextItem key={title} title={title}>
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection> */}

        {/* NETWORK */}
        <MarketingSection title="Network" id="network">
          <Type variant="body1">
            To build intentional community at scale, Pollinator is only open to
            alumni from select residencies, fellowships, and schools. Each
            enrolled participant can also invite up to five friends to join
            Pollinator.
          </Type>
          <Type variant="body1">
            Pollinator’s 2025 pilot program included alumni from 120+
            organizations, including:
          </Type>
          <div className="flex flex-wrap">
            {sendingInstitutionItems
              .filter(({ isActive }) => isActive)
              .map(({ name, location }) => (
                <div
                  key={name}
                  className="w-full pb-2 pr-2 sm:w-1/2 md:w-1/3 md:pb-4 md:pr-4 lg:w-1/4"
                >
                  <Type variant={"h5"} as="span">
                    {name}
                  </Type>
                  <Type variant="caption" className="uppercase">
                    {location}
                  </Type>
                </div>
              ))}
          </div>
          <div id="add-your-institution">
            <CallToAction
              title="Don't see your institution listed?"
              description="We partner with schools, residencies, foundations, and organizations that share our vision for a stronger and more interconnected cultural ecosystem. If the program you’re affiliated with isn't on our list yet, reach out—we’d love to hear from you."
              primaryButtonText="Add your institution"
              onPrimaryClick={onAddYourInstitutions}
            />
          </div>
        </MarketingSection>

        {/* FIT */}
        {/* <MarketingSection title="Is Pollinator for Me?" id="fit">
          <Type variant="body1">Pollinator is for people who…</Type>
          <div className="flex flex-wrap gap-4 md:gap-8">
            {sectionsContent.fit.map(({ title, description }) => (
              <TextItem
                key={title}
                title={title}
                isAccordion
                className="w-full md:w-[calc(50%-1rem)]"
              >
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection> */}

        {/* PRAISE */}
        <MarketingSection title="Praise" id="praise">
          <div className="flex flex-wrap gap-4 md:gap-x-8 md:gap-y-16">
            {sectionsContent.praise.map(({ title, description }) => (
              <TextItem
                key={title}
                title={title}
                className="w-full md:w-[calc(50%-1rem)] md:pr-24"
              >
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection>

        {/* TEAM */}
        <MarketingSection title="Team" id="team">
          <MarketingAbout onHireUsClick={onHireUsClick} />
        </MarketingSection>

        {/* ADVISORS */}
        <MarketingSection title="Advisory Council" id="advisors">
          <div className="flex flex-wrap gap-4 md:gap-8">
            {sectionsContent.advisors.map(({ title, description }) => (
              <TextItem
                key={title}
                title={title}
                className="w-full md:w-[calc(50%-1rem)]"
              >
                {description}
              </TextItem>
            ))}
          </div>
        </MarketingSection>

        {/* NEWSLETTER */}
        <MarketingSection id="newsletter">
          <CallToAction
            title="Get Updates"
            description="Join our newsletter to find out when Pollinator becomes available to you, and to learn about other opportunities."
            theme="light"
            primaryButtonText="Subscribe"
            onPrimaryClick={onNewsletterClick}
          />
        </MarketingSection>

        {/* DONATE */}
        <MarketingSection title="Donate" id="donate">
          <CallToAction
            title="Pollinator is free in 2025 thanks to generous support from public and private grantmakers."
            primaryButtonText="Donate"
            onPrimaryClick={onDonateClick}
          />
          <div className="flex flex-row mt-4 mb-8 items-center justify-center gap-4 h-fit opacity-50 md:h-12 md:gap-12 md:mt-8 md:justify-start">
            <img
              className="h-8 object-contain md:h-full"
              src={halloranLogo}
              alt="Halloran Philanthropies logo"
            />
            <img
              className="h-8 object-contain md:h-full"
              src={jeromeLogo}
              alt="Jerome Foundation logo"
            />
            <img
              className="h-8 object-contain md:h-full"
              src={swissPhilanthropiesLogo}
              alt="Swiss Philanthropies Foundation logo"
            />
          </div>
        </MarketingSection>
      </div>
    </>
  );
}

export default MarketingContent;
